import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, MapPin, Package, BarChart3, Map, Edit, Plus } from "lucide-react";
import { format } from "date-fns";
import type { StockArea, StockPile, StockOverview } from "../../../shared/schema";

// Helper functions
const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'active':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'inactive':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    case 'maintenance':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    default:
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
  }
};

const formatQuantity = (quantity: number | string, unit: string) => {
  const num = typeof quantity === 'string' ? parseFloat(quantity) : quantity;
  return `${num.toLocaleString()} ${unit}`;
};

export default function StockAreaDetails() {
  const params = useParams();
  const areaId = parseInt(params.id!);

  // Fetch area details
  const { data: area, isLoading: areaLoading } = useQuery<StockArea>({
    queryKey: [`/api/stock/areas/${areaId}`],
    enabled: !!areaId,
  });

  // Fetch piles in this area
  const { data: piles = [], isLoading: pilesLoading } = useQuery<StockPile[]>({
    queryKey: [`/api/stock/piles?areaId=${areaId}`],
    enabled: !!areaId,
  });

  // Fetch area overview data
  const { data: areaOverview } = useQuery<StockOverview[]>({
    queryKey: ['/api/stock/overview'],
  });

  if (areaLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading area details...</p>
        </div>
      </div>
    );
  }

  if (!area) {
    return (
      <div className="text-center py-12">
        <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Stock Area Not Found</h2>
        <p className="text-muted-foreground mb-4">The requested stock area could not be found.</p>
        <Link href="/stock-management">
          <Button variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Stock Management
          </Button>
        </Link>
      </div>
    );
  }

  // Find the overview data for this specific area
  const overview = areaOverview?.find(o => o.area.id === areaId);
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link href="/stock-management">
            <Button variant="outline" size="sm" data-testid="button-back-to-stock">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Stock Management
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold" data-testid="text-area-name">{area.name}</h1>
            <p className="text-muted-foreground">Area Code: {area.areaCode}</p>
          </div>
        </div>
        <Badge variant="outline" className={getStatusColor(area.status)} data-testid="badge-area-status">
          {area.status}
        </Badge>
      </div>

      {/* Overview Cards */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Current Stock</CardTitle>
              <Package className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600" data-testid="text-current-stock">
                {formatQuantity(overview.totalCurrentStock, area.capacityUnit || 'kg')}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Available</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600" data-testid="text-available-stock">
                {formatQuantity(overview.totalAvailable, area.capacityUnit || 'kg')}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Allocated</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600" data-testid="text-allocated-stock">
                {formatQuantity(overview.totalAllocated, area.capacityUnit || 'kg')}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Capacity Utilization</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-600" data-testid="text-utilization">
                {overview.totalCapacity > 0 ? 
                  `${((overview.totalCurrentStock / overview.totalCapacity) * 100).toFixed(1)}%` : 
                  'N/A'
                }
              </div>
              {overview.totalCapacity > 0 && (
                <Progress 
                  value={(overview.totalCurrentStock / overview.totalCapacity) * 100} 
                  className="mt-2"
                />
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="details" className="space-y-4">
        <TabsList>
          <TabsTrigger value="details">Area Details</TabsTrigger>
          <TabsTrigger value="piles">Stock Piles ({piles.length})</TabsTrigger>
          <TabsTrigger value="location">Location & Geo-fence</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Area Information</CardTitle>
              <CardDescription>Basic details about this stock area</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Area Code</label>
                    <p className="text-sm" data-testid="text-detail-area-code">{area.areaCode}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Type</label>
                    <p className="text-sm capitalize" data-testid="text-detail-area-type">{area.areaType}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Status</label>
                    <p className="text-sm" data-testid="text-detail-status">{area.status}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Access Level</label>
                    <p className="text-sm capitalize" data-testid="text-detail-access">{area.accessLevel || 'Standard'}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Maximum Capacity</label>
                    <p className="text-sm" data-testid="text-detail-capacity">
                      {area.maxCapacity ? formatQuantity(area.maxCapacity, area.capacityUnit || 'kg') : 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Created</label>
                    <p className="text-sm" data-testid="text-detail-created">
                      {area.createdAt ? format(new Date(area.createdAt), 'PPP') : 'Unknown'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Last Updated</label>
                    <p className="text-sm" data-testid="text-detail-updated">
                      {area.updatedAt ? format(new Date(area.updatedAt), 'PPP') : 'Never'}
                    </p>
                  </div>
                </div>
              </div>
              {area.description && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Description</label>
                  <p className="text-sm mt-1" data-testid="text-detail-description">{area.description}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="piles" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Stock Piles</CardTitle>
                  <CardDescription>All piles located in this area</CardDescription>
                </div>
                <Button size="sm" data-testid="button-add-pile">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Pile
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {pilesLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading piles...</p>
                </div>
              ) : piles.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Pile Code</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Material</TableHead>
                      <TableHead>Current Stock</TableHead>
                      <TableHead>Available</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {piles.map((pile) => {
                      const available = parseFloat(pile.currentQuantity.toString()) - parseFloat(pile.reservedQuantity.toString());
                      return (
                        <TableRow key={pile.id}>
                          <TableCell className="font-medium" data-testid={`text-pile-code-${pile.id}`}>
                            {pile.pileCode}
                          </TableCell>
                          <TableCell data-testid={`text-pile-name-${pile.id}`}>{pile.name}</TableCell>
                          <TableCell>
                            <div>
                              <div data-testid={`text-pile-material-${pile.id}`}>{pile.materialType}</div>
                              {pile.materialGrade && (
                                <div className="text-xs text-muted-foreground">Grade: {pile.materialGrade}</div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell data-testid={`text-pile-current-${pile.id}`}>
                            {formatQuantity(pile.currentQuantity, pile.unit)}
                          </TableCell>
                          <TableCell data-testid={`text-pile-available-${pile.id}`}>
                            {formatQuantity(available, pile.unit)}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={getStatusColor(pile.status)} data-testid={`badge-pile-status-${pile.id}`}>
                              {pile.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button variant="outline" size="sm" data-testid={`button-edit-pile-${pile.id}`}>
                              <Edit className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8">
                  <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Piles Found</h3>
                  <p className="text-muted-foreground mb-4">No stock piles are currently located in this area.</p>
                  <Button data-testid="button-create-first-pile">
                    <Plus className="h-4 w-4 mr-2" />
                    Create First Pile
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="location" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Location & Geographic Information</CardTitle>
              <CardDescription>Geographic boundaries and location data for this area</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Center Coordinates</label>
                  <p className="text-sm" data-testid="text-coordinates">
                    {area.centerLatitude && area.centerLongitude 
                      ? `${area.centerLatitude}, ${area.centerLongitude}`
                      : 'Not specified'
                    }
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Geo-fence</label>
                  <p className="text-sm" data-testid="text-geofence">
                    {area.geoFence ? 'Defined' : 'Not defined'}
                  </p>
                </div>
              </div>
              <div className="h-64 bg-muted rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <Map className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Map view coming soon</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}